<template>
    <div>
        <v-card class="pb-2" :loading="loading">
            <v-card-title class="headline">
                <v-btn small icon @click="close" class="mr-2 ml-n3">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                WORK ORDER CLOSING PROCESS</v-card-title
            >
            <v-card-text class="pb-1 pt-3">
                <v-list dense>
                    <v-list-item
                        v-for="(task, index) in tasks"
                        :key="index"
                        class="pa-0"
                    >
                        <v-list-item-content class="pa-0">
                            <v-list-item-title class="d-flex">
                                <p class="mr-4 text-body-1">
                                    <v-icon class="mr-2">{{ task.icon }}</v-icon
                                    >{{ task.name }}
                                </p>
                                <div>
                                    <v-progress-circular
                                        v-if="task.loading"
                                        indeterminate
                                        size="24"
                                        width="2"
                                    ></v-progress-circular>
                                    <v-icon
                                        v-if="!task.loading && task.checked"
                                        color="green"
                                        >mdi-check-circle</v-icon
                                    >
                                    <v-icon
                                        v-if="!task.loading && !task.checked"
                                        color="red"
                                        >mdi-close-circle</v-icon
                                    >
                                </div>
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </v-card-text>

            <v-card-actions>
                <v-btn color="green white--text" @click="startTaskChecking"
                    ><v-icon size="20" class="mx-0"> mdi-sync </v-icon
                    >RESET</v-btn
                >
                <v-btn
                    color="orange white--text"
                    @click="generateValidationReport"
                    :disabled="loading"
                    class="ml-2"
                >
                    <v-icon size="20" class="mx-0"> mdi-file-excel </v-icon
                    >REPORT
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                    color="primary"
                    :disabled="
                        loading ||
                            (pendingTask &&
                                !closeWOWithPendingItemsPermission) ||
                            (!pendingTask && !closeWorkOrderPermission)
                    "
                    @click="selectAction"
                    >CLOSE WORK ORDER</v-btn
                >
            </v-card-actions>
        </v-card>

        <!-- Pending Items Confirmation Dialog -->
        <v-dialog v-model="pendingItemsAlert" max-width="400px" persistent>
            <v-card>
                <v-card-title class="headline">
                    <span>Confirm Close Work Order</span>
                </v-card-title>
                <v-card-text>
                    <p class="text-body-1">
                        There are pending items in this work order. Are you sure
                        you want to close it?
                    </p>
                    <v-textarea
                        v-model="closureNotes"
                        outlined
                        rows="3"
                        label="Closure notes (optional)"
                        hide-details="auto"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        @click="pendingItemsAlert = false"
                        :disabled="loading"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        color="primary"
                        @click="sendToClosing"
                        :loading="loading"
                    >
                        Confirm
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import * as XLSX from 'xlsx/xlsx.mjs'

export default {
    name: 'WorkOrderClosingProcess',
    props: {
        workOrder: {
            type: Object,
            required: true,
        },
        settingProcesses: {
            type: Object,
            required: true,
        },
        partNumbers: {
            type: Array,
            required: true,
        },
        closeWorkOrderPermission: {
            type: Boolean,
            required: false,
        },
        closeWOWithPendingItemsPermission: {
            type: Boolean,
            required: false,
        },
    },
    data() {
        return {
            tasks: [
                {
                    name: 'Validating Production',
                    icon: 'mdi-factory',
                    checked: false,
                    loading: true,
                },
                {
                    name: 'Validating QA Reviewing',
                    icon: 'mdi-check-bold',
                    checked: false,
                    loading: true,
                },
                {
                    name: 'Validating Packing lists',
                    icon: 'mdi-package-variant',
                    checked: false,
                    loading: true,
                },
            ],
            woProduction: {},
            qualityValidation: {},
            packingValidation: {},
            pendingWOAndPL: {},
            loading: false,
            processesData: {},
            partNumbersCodes: {},
            pendingItemsAlert: false,
            closureNotes: '',
        }
    },
    computed: {
        pendingTask() {
            for (const task of this.tasks) {
                if (!task.checked && !task.loading) {
                    return true
                }
            }
            return false
        },
    },
    async mounted() {
        try {
            // get processes names
            if (this.settingProcesses?.processes?.length > 0) {
                this.settingProcesses.processes.forEach(process => {
                    this.processesData[process.id] = process.name
                })
            }
            // get partnumber codes
            if (this.partNumbers.length > 0) {
                this.partNumbers.forEach(partNumber => {
                    this.partNumbersCodes[partNumber.id] = partNumber.code
                })
            }
            await this.startTaskChecking()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        // redirects to the appropriate action based on the permissions and the pending tasks
        async selectAction() {
            try {
                if (
                    this.pendingTask &&
                    this.closeWOWithPendingItemsPermission
                ) {
                    this.pendingItemsAlert = true
                    return
                }
                return await this.sendToClosing()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        async sendToClosing() {
            try {
                this.loading = true
                await API.closeWorkOrder({
                    workOrderId: this.workOrder.id,
                    notes: this.closureNotes || '',
                    notificationId: 'ar0vX28u4hadZW2pVn9n',
                })
                this.$emit('closeDialog', true)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.pendingItemsAlert = false
                this.loading = false
            }
        },
        close() {
            this.$emit('closeDialog')
        },
        async generateValidationReport() {
            try {
                this.loading = true
                const workbook = XLSX.utils.book_new()

                // Summary sheet
                const summaryData = this.createSummaryData()
                const summarySheet = XLSX.utils.aoa_to_sheet(summaryData)
                XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary')

                // Production sheet
                if (this.woProduction) {
                    const productionData = this.createProductionData()
                    const sheet = XLSX.utils.aoa_to_sheet(productionData)
                    XLSX.utils.book_append_sheet(workbook, sheet, 'Production')
                }

                // Quality sheet
                if (this.qualityValidation) {
                    const qualityData = this.createQualityData()
                    const sheet = XLSX.utils.aoa_to_sheet(qualityData)
                    XLSX.utils.book_append_sheet(workbook, sheet, 'Quality')
                }

                // Packing sheet
                if (this.packingValidation) {
                    const packingData = this.createPackingData()
                    const sheet = XLSX.utils.aoa_to_sheet(packingData)
                    XLSX.utils.book_append_sheet(workbook, sheet, 'Packing')
                }

                const filename = `WO-${this.workOrder.code}-Closing-Report.xlsx`
                XLSX.writeFile(workbook, filename)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        createProductionData() {
            const data = [['Processes', 'Missing Quantity']]
            let total = 0

            if (this.woProduction.pendingProcesses) {
                Object.entries(this.woProduction.pendingProcesses).forEach(
                    ([processId, qty]) => {
                        const processName =
                            this.processesData[processId] || 'not found'
                        data.push([processName, qty])
                        total += qty
                    }
                )
            }

            data.push(['Total', total])
            return data
        },
        createQualityData() {
            const data = [['Part Numbers', 'Missing Quantity']]
            let total = 0

            if (this.qualityValidation.pendingPartNumbers) {
                Object.entries(
                    this.qualityValidation.pendingPartNumbers
                ).forEach(([partNumberId, qty]) => {
                    // Get part number code - should always exist
                    const partNumberCode =
                        this.partNumbersCodes[partNumberId] || 'not found'
                    data.push([partNumberCode, qty])
                    total += qty
                })
            }

            data.push(['Total', total])
            return data
        },
        createPackingData() {
            const data = [['Packing']]
            const missingQty = this.packingValidation.missingQty || 0
            data.push(['Total Missing to be packed', missingQty])
            return data
        },
        createSummaryData() {
            const data = [
                [
                    'Part Numbers',
                    'Pending Production',
                    'Pending Quality',
                    'Pending Packing',
                ],
            ]

            // Get all unique part numbers from work order
            const partNumbers = this.workOrder.partNumbers || {}
            const partNumberIds = Object.keys(partNumbers)

            partNumberIds.forEach(partNumberId => {
                const partNumberCode =
                    this.partNumbersCodes[partNumberId] || 'not found'

                // Get pending production quantity
                let pendingProduction = 0
                if (this.woProduction.missingQtyPerPartnumber) {
                    pendingProduction =
                        this.woProduction.missingQtyPerPartnumber[
                            partNumberId
                        ] || 0
                }

                // Get pending quality quantity
                let pendingQuality = 0
                if (this.qualityValidation.pendingPartNumbers) {
                    pendingQuality =
                        this.qualityValidation.pendingPartNumbers[
                            partNumberId
                        ] || 0
                }

                // Get pending packing quantity
                let pendingPacking = 0
                if (this.packingValidation.missingQtyPerPartnumber) {
                    pendingPacking =
                        this.packingValidation.missingQtyPerPartnumber[
                            partNumberId
                        ] || 0
                }

                data.push([
                    partNumberCode,
                    pendingProduction,
                    pendingQuality,
                    pendingPacking,
                ])
            })

            return data
        },
        async startTaskChecking() {
            try {
                this.loading = true
                for (const index in this.tasks) {
                    this.$set(this.tasks, index, {
                        ...this.tasks[index],
                        loading: true,
                    })
                    if (index == 0) {
                        this.woProduction = await API.validateWOProduction(
                            this.workOrder.id
                        )
                        this.$set(this.tasks, index, {
                            ...this.tasks[index],
                            checked: this.woProduction.isValid || false,
                            loading: false,
                        })
                    } else if (index == 1) {
                        this.qualityValidation = await API.validateWOQuality(
                            this.workOrder.id
                        )
                        this.$set(this.tasks, index, {
                            ...this.tasks[index],
                            checked: this.qualityValidation.isValid || false,
                            loading: false,
                        })
                    } else if (index == 2) {
                        this.packingValidation = await API.validateWOPackingLists(
                            this.workOrder.id
                        )
                        this.$set(this.tasks, index, {
                            ...this.tasks[index],
                            checked: this.packingValidation.isValid || false,
                            loading: false,
                        })
                    }
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style scoped>
.v-list-item {
    margin-bottom: 4px;
}
</style>
